import type React from 'react';

import type { MultiSelectOption } from '@rtkwlf/fenrir-react';
import { ScannerStatus, SensorStatus } from '@rtkwlf/rendall';
import type { OrganizationSiteV2 } from '@rtkwlf/rendall';
import type { TFunction } from 'i18next';
import type { QueryParamConfigMap } from 'use-query-params';

import { isValidIp } from '@packages/utils/ip/ip';
import type { FilterObjType, FilterType } from 'ae-components/filter/Filter';

import type { ScanExclusionType } from '../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';

import type { EnrichedScannerRow } from './use-scanners-table-data/use-scanners-table-data';

export interface ScannerRowSchema extends EnrichedScannerRow {
  searchField?: string;
}

export type ColumnFilters = Record<string, string[] | string>;

const productTypes = ['virtual', 'physical'];
const statusValues = Object.values(ScannerStatus);

export const deploymentStatuses = new Set<ScannerStatus>([
  ScannerStatus.New,
  ScannerStatus.PreparingToShip,
  ScannerStatus.Shipped,
  ScannerStatus.Staged,
]);

const getLocaleKey = (input: string): string => {
  if (input === 'status') return 'badge';
  if (input === 'type') return 'productType';
  return input;
};

export const getLocaleTypes = (t: TFunction): string[] =>
  productTypes.map((s) => t(`telemetryScanners:productType.${s}`));

const getTypeQuery = (values: string[], t: TFunction): string[] =>
  values.map(
    (v) =>
      productTypes.find((s) => t(`telemetryScanners:productType.${s}`) === v) ||
      '',
  );

const getStatusQuery = (values: string[], t: TFunction): string[] =>
  values.map(
    (v) =>
      statusValues.find((s) => t(`telemetryScanners:badge.${s}`) === v) || '',
  );

export const getFilter = (
  input: QueryParamConfigMap,
  t: TFunction,
): FilterObjType => {
  const output: FilterObjType = {};
  Object.entries(input).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      switch (key) {
        case 'search':
          output[key] = values.toString();
          break;
        case 'siteName':
          output[key] = values;
          break;
        default:
          output[key] = values.map((v) =>
            t(`telemetryScanners:${getLocaleKey(key)}.${v}`),
          );
          break;
      }
    }
  });

  return output;
};

export const getQuery = (input: FilterObjType, t: TFunction): FilterObjType => {
  const output: FilterObjType = {};
  Object.entries(input).forEach(([key, values]) => {
    if (key === 'search') {
      output[key] = values;
    } else if (Array.isArray(values)) {
      switch (key) {
        case 'status':
          output[key] = getStatusQuery(values, t);
          break;
        case 'type':
          output[key] = getTypeQuery(values, t);
          break;
        case 'siteName':
          output[key] = values;
          break;
      }
    }
  });
  return output;
};

export const getScannersFilter = (
  t: TFunction,
  sites?: OrganizationSiteV2[],
  scannerActivateFF?: boolean,
): FilterType[] => [
  {
    field: 'status',
    isSingleSelect: true,
    titleKey: t('telemetryScanners:status'),
    values: statusValues
      .filter((s) => scannerActivateFF || !deploymentStatuses.has(s))
      .map((s) => t(`telemetryScanners:badge.${s}`)),
  },
  {
    field: 'type',
    isSingleSelect: true,
    titleKey: t('telemetryScanners:type'),
    values: getLocaleTypes(t),
  },
  {
    field: 'siteName',
    isSingleSelect: true,
    titleKey: t('telemetryScanners:siteName'),
    values: sites ? sites.map((s) => s.name) : [],
  },
];

type ScannerFilters = {
  search?: string;
  status?: ScannerStatus[];
  type?: string[];
  siteName?: string[];
};

export const filterScannersRows = (
  t: TFunction,
  rows: ScannerRowSchema[],
  { search, status, type, siteName }: ScannerFilters,
) => {
  let res = rows;
  if (search) {
    const s = search.toLocaleLowerCase();
    res = res.filter((row) => row.searchField?.includes(s));
  }

  if (status && status.length > 0) {
    res = res.filter((row) => getStatusQuery(status, t).includes(row.status));
  }

  if (type && type.length > 0) {
    res = res.filter((row) => getTypeQuery(type, t).includes(row.type));
  }

  if (siteName && siteName.length > 0) {
    res = res.filter((row) => siteName.includes(row.siteName));
  }

  return res;
};

export const handleOptionCreate = (
  newOption: string,
  updateFilterInput: (filterString: string) => void,
  setAllOptions: (value: React.SetStateAction<MultiSelectOption[]>) => void,
  selectedOptions: MultiSelectOption[],
  handleChange: (options: MultiSelectOption[]) => void,
) => {
  if (newOption.trim().length === 0) return;

  // do not add invalid target to the option list
  if (!isValidIp(newOption)) {
    handleChange([...selectedOptions, { label: newOption, value: newOption }]);
    updateFilterInput('');

    return;
  }

  setAllOptions((prevOptionsArr) => [
    ...prevOptionsArr,
    {
      label: newOption,
      value: newOption,
    },
  ]);
  handleChange([...selectedOptions, { label: newOption, value: newOption }]);

  // Updating filter string after create option success
  updateFilterInput('');
};

export const scannerFilterList = ['search', 'status', 'type', 'siteName'];

export const getPortBlockRules = (items: ScanExclusionType[]) => {
  if (items.length === 0) {
    return [];
  }
  return items
    .filter((item: ScanExclusionType) => item.ports)
    .map((item: ScanExclusionType) => {
      return {
        target: item.ipRange,
        ports: item.ports.split(/,\s*/),
      };
    });
};

export const scannerStatusToSensorStatus = (status: ScannerStatus) => {
  switch (status) {
    case ScannerStatus.Degraded:
    case ScannerStatus.Disconnected:
    case ScannerStatus.Disabled:
    case ScannerStatus.Misconfigured:
      return SensorStatus.Unhealthy;
    case ScannerStatus.AwaitingActivation:
      return SensorStatus.AwaitingActivation;
    case ScannerStatus.Connected:
      return SensorStatus.Healthy;
    case ScannerStatus.Scanning:
    case ScannerStatus.Idle:
      return SensorStatus.Staged;
    case ScannerStatus.Shipped:
      return SensorStatus.Shipped;
  }
};
